import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, X, ExternalLink, Eye, EyeOff } from "lucide-react";
import { API, authHeaders, formatApiError } from "./AdminPage";
import { ImageField } from "./ImageField";
import { resolveMedia } from "../../siteContext";
import { useAdminT } from "./adminI18n";

const EMPTY = {
  slug: "", title: "", excerpt: "", content: "", cover_image: "",
  is_published: true, title_en: "", excerpt_en: "", content_en: "",
};

const inputCls =
  "w-full bg-luxury-black border border-luxury-border text-luxury-bone px-4 py-3 focus:outline-none focus:border-luxury-gold";

const Field = ({ label, children }) => (
  <div>
    <label className="block text-sm text-luxury-gray mb-2">{label}</label>
    {children}
  </div>
);

export const PostsTab = () => {
  const { t } = useAdminT();
  const [posts, setPosts] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () =>
    axios.get(`${API}/admin/posts`, { headers: authHeaders() }).then((res) => setPosts(res.data)).catch(() => {});

  useEffect(() => {
    load();
  }, []);

  const startEdit = (p) => {
    setForm({
      slug: p.slug, title: p.title, excerpt: p.excerpt || "", content: p.content || "", cover_image: p.cover_image || "",
      is_published: p.is_published !== false, title_en: p.title_en || "", excerpt_en: p.excerpt_en || "", content_en: p.content_en || "",
    });
    setEditingId(p.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/admin/posts/${editingId}`, form, { headers: authHeaders() });
        toast.success(t("تم تحديث المقال"));
      } else {
        await axios.post(`${API}/admin/posts`, form, { headers: authHeaders() });
        toast.success(t("تم نشر المقال"));
      }
      setForm(EMPTY);
      setEditingId(null);
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t("حذف هذا المقال نهائياً؟"))) return;
    await axios.delete(`${API}/admin/posts/${id}`, { headers: authHeaders() });
    toast.success(t("تم الحذف"));
    load();
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div data-testid="admin-posts-tab">
      <div className="flex items-center justify-between mb-8">
        <h2 className="font-serif text-3xl text-luxury-bone">المدونة ({posts.length})</h2>
        <button
          data-testid="post-add-button"
          onClick={() => { setForm(EMPTY); setEditingId(null); setShowForm(!showForm); }}
          className="flex items-center gap-2 bg-luxury-gold text-luxury-black font-semibold px-5 py-2.5 rounded-full text-sm hover:bg-luxury-goldLight transition-colors"
        >
          <Plus className="w-4 h-4" strokeWidth={2} />
          {t("مقال جديد")}
        </button>
      </div>

      {showForm && (
        <form data-testid="post-form" onSubmit={save} className="bg-luxury-surface border border-luxury-border p-8 mb-10 space-y-5">
          <div className="flex items-center justify-between">
            <h3 className="font-serif text-2xl text-luxury-gold">{editingId ? "تعديل المقال" : "مقال جديد"}</h3>
            <button type="button" onClick={() => setShowForm(false)} className="text-luxury-gray hover:text-luxury-bone" aria-label={t("إغلاق")}>
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label={t("الرابط (بالإنجليزية، مثال: self-love-journey) *")}>
              <input data-testid="post-slug-input" dir="ltr" value={form.slug} onChange={set("slug")} className={inputCls} required placeholder="self-love-journey" />
            </Field>
            <Field label={t("عنوان المقال *")}><input data-testid="post-title-input" value={form.title} onChange={set("title")} className={inputCls} required /></Field>
            <Field label="Title (EN)"><input dir="ltr" value={form.title_en} onChange={set("title_en")} className={inputCls} /></Field>
            <Field label={t("مقتطف قصير (يظهر في البطاقات)")}><input data-testid="post-excerpt-input" value={form.excerpt} onChange={set("excerpt")} className={inputCls} /></Field>
            <Field label="Excerpt (EN)"><input dir="ltr" value={form.excerpt_en} onChange={set("excerpt_en")} className={inputCls} /></Field>
          </div>
          <Field label={t("نص المقال الكامل")}><textarea data-testid="post-content-input" value={form.content} onChange={set("content")} rows={10} className={inputCls} /></Field>
          <Field label="Content (EN)"><textarea dir="ltr" value={form.content_en} onChange={set("content_en")} rows={6} className={inputCls} /></Field>
          <ImageField testid="post-cover-field" label={t("صورة الغلاف")} value={form.cover_image} onChange={(v) => setForm({ ...form, cover_image: v })} />
          <label className="flex items-center gap-3 text-luxury-bone text-sm cursor-pointer w-fit">
            <input data-testid="post-published-toggle" type="checkbox" checked={form.is_published} onChange={(e) => setForm({ ...form, is_published: e.target.checked })} className="w-4 h-4 accent-[#D1A05C]" />
            {t("منشور على الموقع")}
          </label>
          <button data-testid="post-save-button" type="submit" disabled={saving} className="flex items-center gap-2 bg-luxury-gold text-luxury-black font-semibold px-8 py-3 rounded-full hover:bg-luxury-goldLight transition-colors disabled:opacity-60">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {editingId ? "حفظ التعديلات" : "نشر المقال"}
          </button>
        </form>
      )}

      <div className="space-y-4">
        {posts.map((p, i) => (
          <div key={p.id} data-testid={`post-row-${i}`} className="bg-luxury-surface border border-luxury-border p-5 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4 min-w-0">
              {p.cover_image ? <img src={resolveMedia(p.cover_image)} alt="" className="w-16 h-16 object-cover rounded-lg border border-luxury-border" /> : null}
              <div className="min-w-0">
                <p className="text-luxury-bone font-semibold truncate">{p.title}</p>
                <p className="text-luxury-gray text-xs" dir="ltr">/blog/{p.slug} · {(p.created_at || "").slice(0, 10)}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-xs px-3 py-1 rounded-full border flex items-center gap-1.5 ${p.is_published !== false ? "border-luxury-gold text-luxury-gold" : "border-luxury-border text-luxury-gray"}`}>
                {p.is_published !== false ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                {p.is_published !== false ? "منشور" : "مسودة"}
              </span>
              <a data-testid={`post-view-${i}`} href={`/blog/${p.slug}`} target="_blank" rel="noopener noreferrer" className="p-2 text-luxury-gray hover:text-luxury-gold transition-colors" aria-label={t("معاينة")}>
                <ExternalLink className="w-4 h-4" strokeWidth={1.5} />
              </a>
              <button data-testid={`post-edit-${i}`} onClick={() => startEdit(p)} className="p-2 text-luxury-gray hover:text-luxury-gold transition-colors" aria-label={t("تعديل")}>
                <Pencil className="w-4 h-4" strokeWidth={1.5} />
              </button>
              <button data-testid={`post-delete-${i}`} onClick={() => remove(p.id)} className="p-2 text-luxury-gray hover:text-red-400 transition-colors" aria-label={t("حذف")}>
                <Trash2 className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
          </div>
        ))}
        {posts.length === 0 && <p className="text-luxury-gray text-sm border border-luxury-border p-8 text-center">{t("لا توجد مقالات بعد — اضغطي «مقال جديد» لكتابة أول مقال.")}</p>}
      </div>
    </div>
  );
};
